import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/ui/form'
import { Textarea } from '@/components/ui/textarea'
import { useCreateSSHKey } from '@/services/sshkeys'
import { Plus } from 'lucide-react'
import { toast } from 'react-hot-toast'

const FormSchema = z.object({
  sshKey: z
    .string()
    .refine((val) => /^ssh-(rsa|dss|ed25519|ecdsa) \S+/.test(val.trim()), {
      message: 'Invalid SSH key',
    }),
})

export const CreateSSHKey = () => {
  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      sshKey: '',
    },
  })

  const { mutateAsync, isPending, reset } = useCreateSSHKey()

  async function onSubmit(data: z.infer<typeof FormSchema>) {
    try {
      await mutateAsync({
        alias: 'default',
        public_key: data.sshKey,
      })

      reset()
      form.reset()
    } catch (error) {
      console.error(error)
      toast.error(JSON.stringify(error))
      // toast.error(error as string)
    }
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="mb-4"
        data-testid="create-ssh-key-form"
      >
        <FormField
          disabled={isPending}
          control={form.control}
          name="sshKey"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Textarea
                  placeholder="Paste your public SSH key here"
                  className="w-full pt-4 min-h-[100px]"
                  data-testid="ssh-key-textarea"
                  {...field}
                />
              </FormControl>

              <FormMessage />
            </FormItem>
          )}
        />
        <Button
          className="mt-4"
          type="submit"
          disabled={isPending}
          data-testid="save-ssh-key-button"
        >
          {isPending ? (
            'Creating...'
          ) : (
            <>
              <Plus className="w-4 h-4 mr-2" />
              Save SSH Key
            </>
          )}
        </Button>
      </form>
    </Form>
  )
}
